import { Logo, Spinner } from "@qn/ui";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthScreen } from "./components/AuthScreen";
import { Require2fa } from "./components/Require2fa";
import { Shell } from "./components/Shell";
import { api, type MeResponse } from "./lib/api";
import { useLocaleDom } from "./lib/i18n";
import { usePrefs } from "./lib/prefs";
import { useApplyTheme, useHydratePrefs, useSettingsQuery } from "./lib/settings";
import { ArtifactsPage } from "./pages/ArtifactsPage";
import { AssistantPage } from "./pages/AssistantPage";
import { ChatPage } from "./pages/ChatPage";
import { ProjectPage } from "./pages/ProjectPage";
import { SharedPage } from "./pages/SharedPage";

const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);

export function App() {
  useLocaleDom();
  useApplyTheme();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api<MeResponse>("/auth/me"),
    staleTime: 60_000,
  });
  // A browser that has never been configured looks like the owner's defaults, even on the sign-in screen.
  const ownerDefaults = me.data?.defaults;
  useEffect(() => {
    if (!ownerDefaults || localStorage.getItem("qn-prefs")) return;
    const patch = Object.fromEntries(
      Object.entries(ownerDefaults).filter(([, v]) => v !== undefined),
    );
    if (Object.keys(patch).length) usePrefs.getState().set(patch);
  }, [ownerDefaults]);
  const settings = useSettingsQuery(!!me.data?.user);
  useHydratePrefs(settings.data?.settings);

  const location = useLocation();
  if (location.pathname.startsWith("/s/"))
    return (
      <Routes>
        <Route path="/s/:token" element={<SharedPage />} />
      </Routes>
    );
  if (me.isLoading) {
    return (
      <div className="grid h-dvh place-items-center">
        <div className="flex flex-col items-center gap-4">
          <Logo size={44} />
          <Spinner />
        </div>
      </div>
    );
  }
  if (me.isError || !me.data?.user) return <AuthScreen me={me.data} />;
  if (me.data.mustSetup2fa) return <Require2fa />;

  return (
    <Routes>
      <Route element={<Shell user={me.data.user} />}>
        <Route index element={<ChatPage />} />
        <Route path="c/:id" element={<ChatPage />} />
        <Route path="artifacts" element={<ArtifactsPage />} />
        <Route path="assistant" element={<AssistantPage />} />
        <Route path="p/:id" element={<ProjectPage />} />
        <Route
          path="settings/:section?"
          element={
            <Suspense
              fallback={
                <div className="grid flex-1 place-items-center">
                  <Spinner />
                </div>
              }
            >
              <SettingsPage user={me.data.user} />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
